// Fetch mutator that Orval's generated React Query hooks call (see orval.config.ts). Mirrors the
// surveyhealth-dashboard apiClient contract, with two differences for this anonymous patient flow:
//   - No bearer token. The patient is not a signed-in WorkOS user; the opaque path token in the URL
//     is the only credential, and it is sent by Orval as a path parameter.
//   - Same-origin by default (base ''). In production CloudFront serves this SPA and reroutes
//     "/v1/public/*" to the consent API Gateway; in dev the Vite proxy does the same. So the built
//     artifact is environment-agnostic.

let baseUrl = ''

/** Called once at app startup (main.tsx). Normally a no-op (same-origin); kept for parity/escape hatch. */
export function configureApiClient(config: { baseUrl?: string }) {
  baseUrl = config.baseUrl ?? ''
}

/**
 * Error thrown on any non-2xx. Carries the HTTP status and the consent API's uniform
 * `{ status: "..." }` discriminator (`expired` / `rate_limited` / `mismatch` / `locked` /
 * `copy_updated` / `not_accepted` / `verification_invalid`) so the UI maps a precise outcome.
 */
export interface ConsentApiError extends Error {
  status: number
  code: string | null
  response: Response
}

async function throwApiError(response: Response): Promise<never> {
  let code: string | null = null
  try {
    const text = await response.text()
    if (text) {
      try {
        const parsed = JSON.parse(text) as { status?: unknown }
        if (typeof parsed.status === 'string') code = parsed.status
      } catch {
        // non-JSON body — leave code null, fall back to HTTP status below
      }
    }
  } catch {
    // ignore body read errors
  }
  throw Object.assign(new Error(code ?? `${response.status} ${response.statusText}`.trim()), {
    status: response.status,
    code,
    response,
  })
}

export const consentApiClient = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
  })

  if (!response.ok) await throwApiError(response)

  // SubmitConsent returns 200 with an empty body; the others return JSON.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** Narrow an unknown caught value to the consent API error shape. */
export function asConsentError(err: unknown): ConsentApiError | null {
  return err && typeof err === 'object' && 'status' in err ? (err as ConsentApiError) : null
}
